import { useId } from "react";
import { tv } from "tailwind-variants";

import { analytics } from "@/config/analytics";
import { useCareplanContext } from "@/domains/careplan/contexts/use-careplan-context";
import { isCurrentProduct } from "@/domains/careplan/factories";
import { useCartStore } from "@/stores";
import { Button, Card, Heading, icons } from "@/ui";

const PERKS = ["Discounted Products", "30-Day Guarantee", "Cancel Anytime"];

const careplanSummaryCardVariants = tv({
  slots: {
    root: "flex flex-col gap-5 px-5 py-6 sm:gap-6 sm:px-10 sm:py-10",
    list: "flex flex-col divide-y divide-line overflow-hidden rounded-xl border border-line",
    row: "flex items-center gap-3 px-3 py-3.5 sm:grid sm:grid-cols-2 sm:items-stretch sm:gap-0 sm:p-0",
    product: "flex min-w-0 items-center gap-3.5 sm:px-4 sm:py-3.5",
    image: "size-11 shrink-0 rounded-lg bg-product-media object-contain p-1",
    name: "text-lg leading-snug font-semibold text-content sm:text-xl",
    reason: "text-base leading-snug text-content-secondary sm:hidden",
    reasonColumn:
      "hidden min-w-0 items-center border-l border-line px-4 py-3.5 text-lg text-content-secondary sm:flex",
    actions: "flex flex-col gap-2.5",
    perks:
      "flex flex-col gap-1 text-base text-content-secondary sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-6 sm:gap-y-2",
    perk: "flex items-center gap-2 sm:whitespace-nowrap",
  },
});

const { actions, image, list, name, perk, perks, product, reason, reasonColumn, root, row } =
  careplanSummaryCardVariants();

type CareplanSummaryCardProps = {
  detailsTarget: HTMLElement | null;
};

export const CareplanSummaryCard = ({ detailsTarget }: CareplanSummaryCardProps) => {
  const careplan = useCareplanContext();
  const openCart = useCartStore((state) => {
    return state.openCart;
  });
  const cartCreationFailed = useCartStore((state) => {
    return state.cartCreationFailed;
  });
  const headingId = useId();

  if (careplan.subscription) {
    return null;
  }

  const products = careplan.productsWithHistory.flatMap((item) => {
    const currentProduct = isCurrentProduct(item);

    return currentProduct ? [currentProduct] : [];
  });

  const handleSeeDetails = () => {
    analytics.track("careplan_summary_details_clicked");

    if (!detailsTarget) {
      return;
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    detailsTarget.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
    detailsTarget.tabIndex = -1;
    detailsTarget.addEventListener(
      "blur",
      () => {
        detailsTarget.removeAttribute("tabindex");
      },
      { once: true },
    );
    detailsTarget.focus({ preventScroll: true });
  };

  const handleViewCart = () => {
    analytics.track("careplan_summary_view_cart_clicked");
    openCart();
  };

  return (
    <Card className={root()}>
      <Heading id={headingId} level={2}>
        Your Products
      </Heading>

      <ul aria-labelledby={headingId} className={list()}>
        {products.map((item) => {
          const reasonText = item.whyRecommended?.title;

          return (
            <li className={row()} key={item.id}>
              <div className={product()}>
                <img alt="" className={image()} src={item.summaryImage ?? item.images[0]} />
                <div className="flex min-w-0 flex-col">
                  <p className={name()}>{item.name}</p>
                  {reasonText ? <p className={reason()}>{reasonText}</p> : null}
                </div>
              </div>
              {reasonText ? <p className={reasonColumn()}>{reasonText}</p> : null}
            </li>
          );
        })}
      </ul>

      <div className={actions()}>
        <Button className="w-full" onClick={handleSeeDetails} size="lg">
          See Product & Plan Details
        </Button>
        {cartCreationFailed ? null : (
          <Button className="w-full" onClick={handleViewCart} size="lg" variant="secondary-dark">
            View/Edit Cart & Check Out
          </Button>
        )}
      </div>

      <ul className={perks()}>
        {PERKS.map((text) => {
          return (
            <li className={perk()} key={text}>
              <icons.CheckCircleIcon aria-hidden="true" className="size-5 shrink-0 text-success" />
              <span>{text}</span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};
